import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from "recharts";
import { colors } from "../../theme/colors";
import { buildMapPlayRadarProfile } from "./profileRecentMatches";
import { EmptyDataState } from "../common/EmptyDataState";
import { HubRounded } from "@mui/icons-material";
import { ProfileChartSkeleton } from "./profileSkeletons";
import { TooltipShell } from "../common/Tooltip";

const AXIS_TICK_SX = {
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: colors.text.secondary,
    lineHeight: 1.2,
    textTransform: "uppercase",
};

function RadarAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-42} y={-10} width={84} height={22}>
                <Typography component="div" sx={{ ...AXIS_TICK_SX, textAlign: "center" }}>
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

function RadarTooltip({ active, payload }) {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload;
    if (!point) {
        return null;
    }

    return (
        <TooltipShell sx={{ minWidth: "8.5rem" }}>
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    lineHeight: 1,
                }}
            >
                {point.subject}
            </Typography>
            <Typography
                sx={{
                    mt: "0.35rem",
                    fontSize: "0.92rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {point.display}
            </Typography>
        </TooltipShell>
    );
}

function SummaryStat({ label, value }) {
    return (
        <Box sx={{ minWidth: 0, textAlign: "center" }}>
            <Typography
                sx={{
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.34)",
                    lineHeight: 1,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    mt: "0.3rem",
                    fontSize: "0.88rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

SummaryStat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
};

export default function MapPlayRadarChart({
    mapsStats = {},
    last25MatchData = {},
    limit = null,
    isLoading = false,
}) {
    const profile = useMemo(
        () => buildMapPlayRadarProfile(mapsStats, last25MatchData, limit),
        [mapsStats, last25MatchData, limit],
    );

    if (isLoading) {
        return <ProfileChartSkeleton height="14rem" statCount={3} />;
    }

    if (!profile) {
        return (
            <EmptyDataState
                title="No profile yet"
                icon={HubRounded}
                minHeight="12rem"
                embedded
            >
                Map play profile unlocks with more matches
            </EmptyDataState>
        );
    }

    const { axes, summary } = profile;
    const ratingDisplay = Number.isFinite(summary.avgRating)
        ? summary.avgRating.toFixed(2)
        : "—";
    const adrDisplay = Number.isFinite(summary.avgAdr) ? String(summary.avgAdr) : "—";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flex: 1,
                width: "100%",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    minWidth: 0,
                    height: { xs: "16rem", md: "17rem" },
                    minHeight: { xs: "16rem", md: "17rem" },
                    maxHeight: { md: "19rem" },
                }}
            >
                <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                    <RadarChart cx="50%" cy="50%" outerRadius="76%" data={axes}>
                        <PolarGrid
                            gridType="polygon"
                            stroke="rgba(255,255,255,0.07)"
                        />
                        <PolarAngleAxis
                            dataKey="subject"
                            tick={RadarAxisTick}
                            tickLine={false}
                        />
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, 100]}
                            tick={false}
                            axisLine={false}
                        />
                        <Tooltip content={<RadarTooltip />} />
                        <Radar
                            dataKey="value"
                            stroke={colors.primary.main}
                            fill={colors.primary.main}
                            fillOpacity={0.18}
                            strokeWidth={2}
                            dot={{
                                r: 3,
                                fill: colors.accent.main,
                                stroke: colors.primary.main,
                                strokeWidth: 1,
                            }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: "0.65rem",
                    mt: "1rem",
                    pt: "1rem",
                    flexShrink: 0,
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                }}
            >
                <SummaryStat label="Win Rate" value={`${summary.winRate}%`} />
                <SummaryStat label="Avg Rating" value={ratingDisplay} />
                <SummaryStat label="Avg ADR" value={adrDisplay} />
            </Box>
        </Box>
    );
}
